import { h } from 'preact'
import { memo } from 'preact/compat'
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import cn from 'classnames'

import BaseContext from 'contexts/base-context'
import { TextareaFieldValue } from 'scripts/type'

import TextAreaUI from './TextArea'
import Screenshots from './Screenshots'
import styles from './index.scss'
import { useParentWindowSize } from 'scripts/hooks'

type Props = {
  fieldCode: string
  screenshotsEnabled: boolean
  placeholder?: string
  onChange?: (value: TextareaFieldValue) => void
  onUploadStart?: () => void
  onUploadEnd?: (data: string) => void
  withBorder?: boolean
  className?: string
}

const Textarea = (props: Props) => {
  const {
    fieldCode,
    screenshotsEnabled,
    placeholder,
    withBorder = true,
    className = '',
    onChange,
    onUploadEnd,
    onUploadStart,
  } = props
  const { isUrlVisit } = useContext(BaseContext)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [focus, setFocus] = useState(false)
  // why not use construct { textareaValue: '', screenshots: [] } ?
  // because text always will be clear when screenshot upload end, but I just don't know why
  const [textareaValue, setTextareaValue] = useState<string>('')
  const [screenshots, setScreenshots] = useState<string[]>([])
  const { windowSizeSm } = useParentWindowSize()

  const showScreenshot = useMemo(() => {
    return !isUrlVisit && screenshotsEnabled && !windowSizeSm
  }, [isUrlVisit, screenshotsEnabled, windowSizeSm])

  useEffect(() => {
    onChange?.({ text: textareaValue, screenshots })
  }, [textareaValue, screenshots])

  useEffect(() => {
    if (withBorder) {
      const onTextAreaFocus = () => setFocus(true)
      const onTextAreaBlur = () => setFocus(false)
      textAreaRef.current?.addEventListener('focus', onTextAreaFocus)
      textAreaRef.current?.addEventListener('blur', onTextAreaBlur)
      return () => {
        textAreaRef.current?.removeEventListener('focus', onTextAreaFocus)
        textAreaRef.current?.removeEventListener('blur', onTextAreaBlur)
      }
    }
  }, [withBorder])

  const handleTextChange = (text: string) => {
    setTextareaValue(text.trim())
  }

  const handleUploadEnd = (screenshot: string) => {
    setScreenshots([screenshot])
    onUploadEnd?.(screenshot)
  }

  return (
    <div
      className={cn(
        styles.textAreaWrapper,
        className,
        showScreenshot && styles.withScreenshot,
        withBorder && styles.withBorder,
        { [styles.focus]: focus }
      )}
    >
      <TextAreaUI onChange={handleTextChange} placeholder={placeholder} ref={textAreaRef} />
      {showScreenshot && (
        <Screenshots fieldCode={fieldCode} onUploadStart={onUploadStart} onUploadEnd={handleUploadEnd} />
      )}
    </div>
  )
}

export default memo(Textarea)
